import {
  entryParadigm,
  entrySenses,
  type CachedLexeme,
  type Lexeme,
  type LexiconEntry,
  type RuleBlock,
} from './lexicon-model'

export const LEXICON_CARD_KINDS = [
  'readAloud',
  'dictation',
  'senseRecall',
  'formRecall',
  'formRecallParadigmCell',
] as const
export type LexiconCardKind = (typeof LEXICON_CARD_KINDS)[number]

export function residueCells(
  entry: CachedLexeme | Lexeme,
  rule?: RuleBlock,
): string[] {
  const predicts =
    rule?.key === entry.inflectionClass && rule.language === entry.language
      ? new Set(rule.predicts)
      : undefined
  return entryParadigm(entry)
    .filter((form) => !predicts?.has(form.cell))
    .map((form) => form.cell)
}

export type MintedCard =
  | { kind: 'readAloud' | 'dictation'; qid: string }
  | {
      kind: 'senseRecall' | 'formRecall'
      qid: string
      senseId: string
    }
  | { kind: 'formRecallParadigmCell'; qid: string; cell: string }

export function mintedCards(
  entry: LexiconEntry,
  rule?: RuleBlock,
): MintedCard[] {
  const cards: MintedCard[] = []
  if (entry.kind === 'lexeme') {
    cards.push(
      { kind: 'readAloud', qid: entry.readAloudQuestionId },
      { kind: 'dictation', qid: entry.dictationQuestionId },
    )
  }

  for (const sense of entrySenses(entry)) {
    if (sense.senseRecallQuestionId) {
      cards.push({
        kind: 'senseRecall',
        qid: sense.senseRecallQuestionId,
        senseId: sense.id,
      })
    }
    if (sense.formRecallQuestionId) {
      cards.push({
        kind: 'formRecall',
        qid: sense.formRecallQuestionId,
        senseId: sense.id,
      })
    }
  }

  if (entry.kind === 'lexeme') {
    const residue = new Set(residueCells(entry, rule))
    for (const form of entryParadigm(entry)) {
      if (residue.has(form.cell) && form.formRecallQuestionId) {
        cards.push({
          kind: 'formRecallParadigmCell',
          qid: form.formRecallQuestionId,
          cell: form.cell,
        })
      }
    }
  }
  return cards
}

export function getLexiconCardType(
  entry: LexiconEntry,
  qid: string | undefined,
): LexiconCardKind | undefined {
  if (!qid) return undefined
  if (entry.kind === 'lexeme') {
    if (entry.readAloudQuestionId === qid) return 'readAloud'
    if (entry.dictationQuestionId === qid) return 'dictation'
  }
  for (const sense of entrySenses(entry)) {
    if (sense.senseRecallQuestionId === qid) return 'senseRecall'
    if (sense.formRecallQuestionId === qid) return 'formRecall'
  }
  if (entry.kind === 'lexeme') {
    for (const form of entryParadigm(entry)) {
      if (form.formRecallQuestionId === qid)
        return 'formRecallParadigmCell'
    }
  }
  return undefined
}

/** Every question identity the entry holds with its kind (the inverse of getLexiconCardType), whether or not it is minted. */
export function entryCardKinds(entry: LexiconEntry): [string, LexiconCardKind][] {
  const kinds: [string, LexiconCardKind][] = []
  if (entry.kind === 'lexeme') kinds.push([entry.readAloudQuestionId, 'readAloud'], [entry.dictationQuestionId, 'dictation'])
  for (const sense of entrySenses(entry)) {
    if (sense.senseRecallQuestionId) kinds.push([sense.senseRecallQuestionId, 'senseRecall'])
    if (sense.formRecallQuestionId) kinds.push([sense.formRecallQuestionId, 'formRecall'])
  }
  if (entry.kind === 'lexeme') for (const form of entryParadigm(entry)) {
    if (form.formRecallQuestionId) kinds.push([form.formRecallQuestionId, 'formRecallParadigmCell'])
  }
  return kinds
}
